import React from "react"
import { useSelector, useDispatch } from "react-redux"

import { LIST, VIEW, ADD, EDIT, DELETE } from "const"
import { deepCopy, hasProjectAccess } from "client/utils"
import {
    PRM_PERMISSIONS,
    StoreType,
    addModal,
    updateProjectPermission,
    setProjectPermissionsAccountGroupSelected,
    setProjectPermissionsProjectGroupSelected,
    setProjectPermissionsProjectUserSelected,
} from "client/store"
import {
    ProjectPermissionRow,
    ProjectPermissionItem,
    PermissionType,
} from "client/store/utils"
import {
    PermissionTableColumn,
    PermissionList,
    PermissionListItem,
    PermissionListAddItem,
    PermissionLabel,
    Permission,
    DeletePermissionButton,
} from "client/components"

import NotAssigned from "./notAssigned"

const Columns = () => {
    const accountGroupList = useSelector((state: StoreType) => state.group.groupList)
    const projectGroupList = useSelector((state: StoreType) => state.projectGroup.groupList)
    const userList = useSelector((state: StoreType) => state.user.userList)

    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const editAccess = hasProjectAccess(PRM_PERMISSIONS, EDIT)

    const permissionFormatter = (
        section: string,
        permissionId: string,
        permissionType: PermissionType,
        permission: ProjectPermissionRow,
    ) => {
        const permissionItem = permission[permissionType]
        if (!permissionItem) return null // unused permission

        const { accountGroups, projectGroups, users } = permissionItem
        const allowedToEdit = editAccess && permission.customizable

        return (
            <PermissionList>
                {
                    accountGroups.map((groupId) => {
                        const group = accountGroupList[groupId]
                        return group && (
                            <PermissionListItem key={groupId}>
                                <PermissionLabel>{`${lang.group}:`}</PermissionLabel>
                                <Permission>{group.name}</Permission>
                                {
                                    allowedToEdit && (
                                        <DeletePermissionButton
                                            onClick={() => {
                                                const newPermissionItem: ProjectPermissionItem = deepCopy(permissionItem)
                                                newPermissionItem.accountGroups = accountGroups.filter((id) => id !== groupId)
                                                dispatch(updateProjectPermission(
                                                    section,
                                                    permissionId,
                                                    permissionType,
                                                    newPermissionItem,
                                                ))
                                            }}
                                        />
                                    )
                                }
                            </PermissionListItem>
                        )
                    })
                }
                {
                    projectGroups.map((groupId) => {
                        const group = projectGroupList[groupId]
                        return group && (
                            <PermissionListItem key={groupId}>
                                <PermissionLabel>{`${lang.group}:`}</PermissionLabel>
                                <Permission>{group.name}</Permission>
                                {
                                    allowedToEdit && (
                                        <DeletePermissionButton
                                            onClick={() => {
                                                const newPermissionItem: ProjectPermissionItem = deepCopy(permissionItem)
                                                newPermissionItem.projectGroups = projectGroups.filter((id) => id !== groupId)
                                                dispatch(updateProjectPermission(
                                                    section,
                                                    permissionId,
                                                    permissionType,
                                                    newPermissionItem,
                                                ))
                                            }}
                                        />
                                    )
                                }
                            </PermissionListItem>
                        )
                    })
                }
                {
                    users.map((userId) => {
                        const user = userList[userId]
                        return user && (
                            <PermissionListItem key={userId}>
                                <PermissionLabel>{`${lang.user}:`}</PermissionLabel>
                                <Permission>{`${user.firstName} ${user.lastName}`}</Permission>
                                {
                                    allowedToEdit && (
                                        <DeletePermissionButton
                                            onClick={() => {
                                                const newPermissionItem: ProjectPermissionItem = deepCopy(permissionItem)
                                                newPermissionItem.users = users.filter((id) => id !== userId)
                                                dispatch(updateProjectPermission(
                                                    section,
                                                    permissionId,
                                                    permissionType,
                                                    newPermissionItem,
                                                ))
                                            }}
                                        />
                                    )
                                }
                            </PermissionListItem>
                        )
                    })
                }
                {
                    allowedToEdit && (
                        <PermissionListAddItem
                            onClick={() => {
                                dispatch(setProjectPermissionsAccountGroupSelected(null))
                                dispatch(setProjectPermissionsProjectGroupSelected(null))
                                dispatch(setProjectPermissionsProjectUserSelected(null))
                                dispatch(addModal({
                                    key: "notAssignedPermissions",
                                    content: <NotAssigned
                                        section={section}
                                        permissionId={permissionId}
                                        permissionType={permissionType}
                                        permissionItem={permissionItem}
                                    />,
                                }))
                            }}
                        >
                            {`+ ${lang.add}`}
                        </PermissionListAddItem>
                    )
                }
            </PermissionList>
        )
    }

    const cellMinWidth = "200px"
    const columns: PermissionTableColumn[] = [
        {
            field: "name",
            header: lang.name,
            cellMinWidth,
            cellMaxWidth: "300px",
            formatter: (_section, _permissionId, permission: ProjectPermissionRow) => permission.title,
        },
        {
            field: LIST,
            header: lang.list,
            cellMinWidth,
            formatter: (section, permissionId, permission: ProjectPermissionRow) => (
                permissionFormatter(section, permissionId, LIST, permission)
            ),
        },
        {
            field: VIEW,
            header: lang.view,
            cellMinWidth,
            formatter: (section, permissionId, permission: ProjectPermissionRow) => (
                permissionFormatter(section, permissionId, VIEW, permission)
            ),
        },
        {
            field: ADD,
            header: lang.add,
            cellMinWidth,
            formatter: (section, permissionId, permission: ProjectPermissionRow) => (
                permissionFormatter(section, permissionId, ADD, permission)
            ),
        },
        {
            field: EDIT,
            header: lang.edit,
            cellMinWidth,
            formatter: (section, permissionId, permission: ProjectPermissionRow) => (
                permissionFormatter(section, permissionId, EDIT, permission)
            ),
        },
        {
            field: DELETE,
            header: lang.delete,
            cellMinWidth,
            formatter: (section, permissionId, permission: ProjectPermissionRow) => (
                permissionFormatter(section, permissionId, DELETE, permission)
            ),
        },
    ]

    return columns
}
export default Columns
